/**
 * Responsibility: keep the set of terrain tiles around a moving viewer correct.
 *
 * Builds tiles as they come into range, releases them as they fall out of it, and never
 * remembers anything about a tile it has released. That last part is what makes an
 * endless world free: because terrain is a pure function of `(seed, x, z)`, a tile that
 * is regenerated later is byte-identical to the one that was thrown away, so there is
 * nothing worth keeping.
 *
 * ## Why this file has no Three.js import
 *
 * Residency is arithmetic — which tile indices are near a point, and which are not. It
 * has no business knowing what a mesh is. The render layer supplies `mount` and
 * `unmount` callbacks and owns every GPU object; this module only decides when to call
 * them. That keeps the interesting logic testable in Node with counters standing in for
 * meshes.
 *
 * ## Residency is a disc, not a square
 *
 * A tile is resident when the nearest point of its footprint is within `loadDistance`.
 * Measuring to the footprint rather than the tile centre matters: the tile the viewer is
 * standing in is at distance zero regardless of where in the tile they are.
 *
 * Testing distance rather than a tile-index radius is a real saving, not fussiness. A
 * square block wide enough to cover a 7800-unit load distance at 1600-unit tiles is
 * 11 x 11 = 121 tiles, and its corners sit over 11,000 units away — past the fog limit,
 * so entirely black and entirely wasted. The disc is about 89 tiles for identical
 * visible coverage.
 */



/**
 * Which tile index contains a world coordinate.
 *
 * Floor rather than truncation, so the sequence stays continuous through zero: without
 * it, tiles either side of the origin would both claim index 0 and one would never load.
 *
 * @param {number} coordinate - World X or Z.
 * @param {number} size - Tile edge length.
 * @returns {number} Tile index, may be negative.
 */
export function tileIndexFor(coordinate, size) {
  return Math.floor(coordinate / size);
}

/**
 * Map key for a tile index pair.
 *
 * @param {number} tileX
 * @param {number} tileZ
 * @returns {string}
 */
export function tileKey(tileX, tileZ) {
  return `${tileX},${tileZ}`;
}

/**
 * Distance from a point to the nearest edge of a tile's footprint, ignoring elevation.
 *
 * Returns 0 when the point is inside the footprint. Distance is horizontal only, because
 * that is what fog and streaming care about — altitude does not change which terrain is
 * around you.
 *
 * @param {number} viewerX
 * @param {number} viewerZ
 * @param {number} tileX - Tile index, not a world coordinate.
 * @param {number} tileZ - Tile index, not a world coordinate.
 * @param {number} size - Tile edge length.
 * @returns {number}
 */
export function distanceToTile(viewerX, viewerZ, tileX, tileZ, size) {
  const minX = tileX * size;
  const minZ = tileZ * size;

  // Zero on the axis when the viewer is between the tile's bounds on that axis.
  const dx = Math.max(minX - viewerX, 0, viewerX - (minX + size));
  const dz = Math.max(minZ - viewerZ, 0, viewerZ - (minZ + size));

  return Math.hypot(dx, dz);
}

/**
 * Create a tile manager.
 *
 * Geometry generation is injected rather than done here. A tile is terrain plus whatever
 * structures stand on it, and assembling that is `tileContent.js`'s job — this module only
 * decides which tiles should exist and when. Keeping it that way is also why the residency
 * tests need no biomes, noise, or palettes at all.
 *
 * @param {object} options
 * @param {number} options.size - Tile edge length in world units.
 * @param {number} options.loadDistance - Build a tile once it is this close.
 * @param {number} options.evictDistance - Release a tile once it is this far. Must exceed
 *   `loadDistance`; the gap is the hysteresis that stops boundary thrashing.
 * @param {number} options.maxBuildsPerUpdate - Build budget per `update` call.
 * @param {(originX: number, originZ: number) => {segmentCount: number}} options.buildTile
 *   Produces one tile's geometry. Receives the tile's minimum corner in absolute world
 *   coordinates, which is what guarantees neighbouring tiles sample identical positions
 *   along a shared edge.
 * @param {(tileData: object, tileX: number, tileZ: number) => any} options.mount
 *   Hands finished tile data to the render layer. Whatever it returns is stored opaquely
 *   and given back to `unmount`.
 * @param {(handle: any, tileX: number, tileZ: number) => void} options.unmount
 *   Releases whatever `mount` created.
 * @returns {object} Manager handle.
 */
export function createTileManager({
  size,
  loadDistance,
  evictDistance,
  maxBuildsPerUpdate,
  buildTile,

  /**
   * Streaming source, which may answer `null` for "not ready yet".
   *
   * Optional, and absent means the previous behaviour exactly: build every tile in place, on this thread. That
   * is the fallback if a worker cannot run, and it is what the residency tests use — none of them care where
   * geometry comes from, so none of them had to change.
   */
  requestTile,

  mount,
  unmount,

  /*
    Injectable so a test can drive the build deadline without waiting in real time. Defaults to the monotonic clock, which
    is what a frame budget has to be measured against — `Date.now` can step backwards.
  */
  now = () => performance.now(),
}) {
  if (!(size > 0)) {
    throw new Error('createTileManager: size must be positive');
  }
  if (!(loadDistance > 0)) {
    throw new Error('createTileManager: loadDistance must be positive');
  }
  if (!(evictDistance > loadDistance)) {
    /*
      Equal thresholds are the subtle version of this bug: a tile sitting exactly at the
      boundary would be built and released on alternating frames indefinitely. Rejecting
      it here means it can never reach a running build.
    */
    throw new Error('createTileManager: evictDistance must be greater than loadDistance');
  }
  if (!Number.isInteger(maxBuildsPerUpdate) || maxBuildsPerUpdate < 1) {
    throw new Error('createTileManager: maxBuildsPerUpdate must be an integer of at least 1');
  }
  if (typeof mount !== 'function' || typeof unmount !== 'function') {
    throw new Error('createTileManager: mount and unmount must both be functions');
  }
  if (typeof buildTile !== 'function') {
    throw new Error('createTileManager: buildTile must be a function');
  }

  /** Resident tiles by key. Values are `{tileX, tileZ, handle, segmentCount}`. */
  const resident = new Map();

  /** Running total, kept incrementally so the debug readout costs nothing to produce. */
  let segmentTotal = 0;

  /**
   * Build one tile and hand it to the render layer.
   *
   * @param {number} tileX
   * @param {number} tileZ
   * @param {(originX: number, originZ: number) => object|null} produce - Where the geometry comes from.
   *   Priming passes the synchronous builder; streaming passes one that may answer `null`.
   * @returns {boolean} Whether a tile was actually mounted.
   */
  function addTile(tileX, tileZ, produce) {
    // Absolute world origin, not a local offset. See the seam-alignment note in `tile.js`.
    const tileData = produce(tileX * size, tileZ * size);

    /*
      **`null` means "not ready yet", and doing nothing is the whole handling.** The candidate scan runs every
      frame over the same window, so an unready tile is simply asked for again next frame — which is why
      moving generation onto a worker needed no queue, no callbacks and no promises here.
    */
    if (tileData === null || tileData === undefined) return false;

    const handle = mount(tileData, tileX, tileZ);

    resident.set(tileKey(tileX, tileZ), {
      tileX,
      tileZ,
      handle,
      segmentCount: tileData.segmentCount,
    });
    segmentTotal += tileData.segmentCount;

    return true;
  }

  /** Release one tile. */
  function removeTile(key, entry) {
    unmount(entry.handle, entry.tileX, entry.tileZ);
    resident.delete(key);
    segmentTotal -= entry.segmentCount;
  }

  /**
   * Bring residency in line with a viewer position.
   *
   * Eviction runs before building so that a step which both releases and builds tiles
   * never holds more geometry than the steady state needs.
   *
   * @param {number} viewerX
   * @param {number} viewerZ
   * @param {number} buildBudget - Maximum tiles to build this pass.
   * @returns {{built: number, evicted: number}}
   */
  function refresh(viewerX, viewerZ, buildBudget, deadline, produce = buildTile) {
    let evicted = 0;

    for (const [key, entry] of resident) {
      if (distanceToTile(viewerX, viewerZ, entry.tileX, entry.tileZ, size) > evictDistance) {
        removeTile(key, entry);
        evicted += 1;
      }
    }

    /*
      Only tiles in this index window can possibly be within loadDistance, so the scan is
      bounded no matter how far the viewer has flown. Derived from the viewer position
      rather than from the previous position, which is what makes an arbitrarily large
      jump (a save load, or a floating-origin rebase in Task 9) work with no special case.
    */
    const minTileX = tileIndexFor(viewerX - loadDistance, size);
    const maxTileX = tileIndexFor(viewerX + loadDistance, size);
    const minTileZ = tileIndexFor(viewerZ - loadDistance, size);
    const maxTileZ = tileIndexFor(viewerZ + loadDistance, size);

    const candidates = [];

    for (let tileX = minTileX; tileX <= maxTileX; tileX += 1) {
      for (let tileZ = minTileZ; tileZ <= maxTileZ; tileZ += 1) {
        if (resident.has(tileKey(tileX, tileZ))) continue;

        const distance = distanceToTile(viewerX, viewerZ, tileX, tileZ, size);
        if (distance <= loadDistance) {
          candidates.push({ tileX, tileZ, distance });
        }
      }
    }

    /*
      Nearest first. When the budget cannot clear the backlog in one pass, the tiles left
      waiting are the furthest away — already fully fogged to black, so the delay cannot
      be seen. Building in scan order instead would sometimes leave a hole underfoot.
    */
    candidates.sort((a, b) => a.distance - b.distance);

    let built = 0;

    /*
      **The budget counts tiles actually mounted, not candidates examined.** With generation on a worker most
      asks answer "not yet", and a budget spent on those would starve the tiles that *are* ready — the nearest
      candidate is the least likely to have finished, so a count-every-ask budget would stall behind it every
      frame. An unready candidate costs nothing and, since asking is what queues the work, offering it is the
      point.
    */
    for (let i = 0; i < candidates.length && built < buildBudget; i += 1) {
      /*
        **A deadline as well as a count.** The count alone says how many tiles may be built; it says nothing about whether
        there is time left to build them, and a tile costs on the order of a whole frame. Checked before each build rather
        than after, because overrunning is the thing being prevented — measuring it afterwards is too late.

        Absent when priming, where the whole point is to ignore the budget and finish.
      */
      if (deadline !== undefined && now() > deadline) break;

      if (addTile(candidates[i].tileX, candidates[i].tileZ, produce)) built += 1;
    }

    return { built, evicted };
  }

  return {
    /**
     * Build every tile in range at once, ignoring the per-update budget.
     *
     * Used at startup and after any discontinuous jump. The budget exists to keep frames
     * smooth during flight, but on the first frame there is no smoothness to protect and
     * a partially built world would be visible, so it is deliberately bypassed.
     *
     * **Builds on this thread even when a worker is available**, and that is the right trade. Priming is
     * about a hundred tiles, so waiting on a worker would mean an empty world for the first second or the
     * world assembling itself in front of the player. A blocking second before the first frame is what
     * already happened and what a player reads as loading.
     *
     * @param {number} viewerX
     * @param {number} viewerZ
     * @returns {{built: number, evicted: number}}
     */
    prime(viewerX, viewerZ) {
      return refresh(viewerX, viewerZ, Number.POSITIVE_INFINITY, undefined, buildTile);
    },

    /**
     * Bring residency in line with the viewer, within the build budget.
     *
     * @param {number} viewerX
     * @param {number} viewerZ
     * @returns {{built: number, evicted: number}}
     */
    update(viewerX, viewerZ, deadline) {
      /*
        Streaming goes through `requestTile` when there is one, which is what keeps a 21 ms tile build out of
        the frame. Priming deliberately does not — see `prime`.
      */
      return refresh(viewerX, viewerZ, maxBuildsPerUpdate, deadline, requestTile ?? buildTile);
    },

    /** How many tiles are currently built. */
    getResidentCount() {
      return resident.size;
    },

    /** Total line segments across all resident tiles. The geometry budget, live. */
    getSegmentCount() {
      return segmentTotal;
    },

    /**
     * Whether a tile index pair is currently built.
     *
     * @param {number} tileX
     * @param {number} tileZ
     * @returns {boolean}
     */
    isResident(tileX, tileZ) {
      return resident.has(tileKey(tileX, tileZ));
    },

    /** Keys of resident tiles, sorted so comparisons do not depend on insertion order. */
    getResidentKeys() {
      return [...resident.keys()].sort();
    },

    /**
     * Release every tile, leaving the manager ready to prime again.
     *
     * This is what a change of seed needs: every resident tile belongs to the old world and would
     * otherwise sit in the scene, correct-looking but generated from a seed that no longer applies.
     * The manager itself holds nothing seed-specific — `buildTile` does — so it survives the change.
     */
    clear() {
      for (const [key, entry] of [...resident]) {
        removeTile(key, entry);
      }
    },

    /** Release every tile on teardown, so nothing is left on the GPU. */
    dispose() {
      this.clear();
    },
  };
}
